import { createSelector } from 'reselect';
import { get as lodashGet } from 'lodash';
import { selectors as macheteReduxSelectors, utils } from 'src/machete-redux';
import constants from 'src/constants';
import appDux from 'src/redux/app/';
import undoDux from 'src/redux/undo/';

const { SPLIT_MODES } = constants.app;
const { metersToInches, inchesToFeetInches } = Hover.ConversionUtils;

const isEditingSplit = (state) => state.designPro.split.isEditingSplit;
const isSplitToolActive = (state) => state.designPro.split.isSplitToolActive;
const isDeletingSplit = (state) => state.designPro.split.isDeletingSplit;

const getSplitMinMaxOffset = (state) => ({
  minOffset: state.designPro.split.splitToolMinOffset,
  maxOffset: state.designPro.split.splitToolMaxOffset,
});
const getSplitOrientation = (state) =>
  state.designPro.split.splitToolOrientation;
const getSplitPercentage = (state) => state.designPro.split.splitPercentage;
const getSplitToolUUIDSToSplit = (state) =>
  state.designPro.split.splitToolNodes;

const getSplitToolOneInchPercentageIncrement = (state) => {
  const { minOffset, maxOffset } = getSplitMinMaxOffset(state);
  const minInches = metersToInches(minOffset);
  const maxInches = metersToInches(maxOffset);
  const totalInches = maxInches - minInches;
  return 1 / totalInches;
};

const getSignLabel = (sign) => (sign === -1 ? '-' : '');

const getSplitToolLabels = (state) => {
  const { minOffset, maxOffset } = getSplitMinMaxOffset(state);
  const splitPercentage = getSplitPercentage(state);
  const totalMin = metersToInches(minOffset);
  const totalMax = metersToInches(maxOffset);
  const currentValue = totalMin + splitPercentage * (totalMax - totalMin);
  const {
    feet: minFeet,
    inches: minInches,
    sign: minSign,
  } = inchesToFeetInches(totalMin);
  const {
    feet: maxFeet,
    inches: maxInches,
    sign: maxSign,
  } = inchesToFeetInches(totalMax);
  const { feet: currFeet, inches: currInches, sign } = inchesToFeetInches(
    currentValue,
  );

  return {
    minOffsetLabel: `${getSignLabel(minSign)}${minFeet}' ${Math.round(
      minInches,
    )}"`,
    maxOffsetLabel: `${getSignLabel(maxSign)}${maxFeet}' ${Math.round(
      maxInches,
    )}"`,
    currentValueLabel: `${getSignLabel(sign)}${currFeet}' ${Math.round(
      currInches,
    )}"`,
    currentValueLabelType2: `${getSignLabel(sign)}${currFeet} ft   ${Math.round(
      currInches,
    )} in`,
  };
};

/**
 * We don't export default this function because it is only used locally within this file.
 * The only reason we need to export it at all is to test it.
 */
export const isOneOfSelectedNodesAlreadySplit = createSelector(
  macheteReduxSelectors.segmentation.getSelectedNodeIds,
  appDux.selectors.getNodeStylesMap,
  // undo operations may cause this selector to cache "stale" data, in that
  // the split status of selected nodes may change, so we add the selector
  // here to cache-bust it and re-check all the nodes
  undoDux.selectors.isOperationInProgress,
  (selectedNodeIds, stylesMap, _) =>
    /**
     * using Array.some() to break out early if we find a not present split child
     * we do this because utils.getNode() is very expensive
     */
    selectedNodeIds.length > 0 &&
    !selectedNodeIds.some((nodeID) => {
      if (stylesMap[nodeID].category !== 'walls') return true;
      const node = utils.getNode(nodeID);
      const splitChild = node.children.find((child) => child.type === 'split');
      const isSplitChildPresent = lodashGet(
        splitChild.properties.find((property) => property.type === 'present'),
        'value',
      );
      return !isSplitChildPresent;
    }),
);

const isEditSplitButtonVisible = createSelector(
  isSplitToolActive,
  isOneOfSelectedNodesAlreadySplit,
  (isSplitActive, doSplitChildrenExist) =>
    !isSplitActive && doSplitChildrenExist,
);

const isSplitToolButtonEnabled = createSelector(
  isEditSplitButtonVisible,
  isSplitToolActive,
  macheteReduxSelectors.segmentation.getSelectedNodeIds,
  appDux.selectors.getNodeStylesMap,
  (isEditButtonVisible, isSplitActive, selectedNodeIds, stylesMap) => {
    if (selectedNodeIds.length === 0) {
      return false;
    }

    const isSplitVisible = isEditButtonVisible || isSplitActive;
    const areOnlyWallsAreSelected = selectedNodeIds.every((nodeID) => {
      const isNodeCategoryWall = stylesMap[nodeID].category === 'walls';
      return isNodeCategoryWall;
    });
    const areAnySelectedNodesCorrupted = selectedNodeIds.some((uuid) => {
      let isCorrupt = false;
      try {
        isCorrupt = utils.getNode(uuid).isCorrupt();
        // eslint-disable-next-line no-empty
      } catch {}

      return isCorrupt;
    });

    if (!areOnlyWallsAreSelected) {
      return false;
    }
    if (isSplitVisible) {
      return false;
    }
    if (areAnySelectedNodesCorrupted) {
      return false;
    }

    return true;
  },
);

const isVerticalSplitEnabled = createSelector(
  macheteReduxSelectors.segmentation.getSelectedNodeIds,
  (selectedNodeIds) => selectedNodeIds.length === 1,
);

const isOrientationChangeEnabled = createSelector(
  isOneOfSelectedNodesAlreadySplit,
  isVerticalSplitEnabled,
  (doSplitChildrenExist, isVerticalEnabled) =>
    !doSplitChildrenExist && isVerticalEnabled,
);

const isDeleteSplitButtonEnabled = createSelector(
  isDeletingSplit,
  isEditingSplit,
  (isDeleting, isEditing) => isEditing && !isDeleting,
);

const getOrientationIconPath = createSelector(
  getSplitOrientation,
  (orientation) => {
    return orientation === SPLIT_MODES.HORIZONTAL
      ? require('src/assets/images/toolSplitVert_enabled.svg')
      : require('src/assets/images/toolSplitHoriz_enabled.svg');
  },
);

const isSplitConfirmDisabled = createSelector(
  isDeletingSplit,
  getSplitPercentage,
  (isDeletingSplit, splitPercentage) =>
    !isDeletingSplit && (!splitPercentage || splitPercentage === 1),
);

const shouldSelectChildrenAfterSplit = createSelector(
  getSplitOrientation,
  getSplitPercentage,
  (splitOrientation, splitPercentage) => {
    // NS-786, NS-1173, the smallest created split should be select after confirm
    // NOTE: orientation must be accounted, since vertical splits start from
    // the left.
    return splitOrientation === SPLIT_MODES.HORIZONTAL
      ? splitPercentage > 0.5
      : splitPercentage <= 0.5;
  },
);

export default {
  isDeletingSplit,
  getOrientationIconPath,
  getSplitMinMaxOffset,
  getSplitOrientation,
  getSplitPercentage,
  getSplitToolLabels,
  getSplitToolOneInchPercentageIncrement,
  getSplitToolUUIDSToSplit,
  isDeleteSplitButtonEnabled,
  isEditingSplit,
  isEditSplitButtonVisible,
  isOrientationChangeEnabled,
  isSplitConfirmDisabled,
  isSplitToolButtonEnabled,
  isSplitToolActive,
  isVerticalSplitEnabled,
  shouldSelectChildrenAfterSplit,
};
